import {
    APP,
    ALIGN,
    VALIGN,
} from '_pixiSrc/globals';
import I18 from '_pixiSrc/I18';
import Sprite from '_pixiSrc/display/Sprite';
import Static from '../libraries/Static';

import UI from './UI';
import GameData from './GameData';
import GambleGame from '../gameEngine/GambleGame';

import MobileSpinButton from './MobileSpinButton';
import SwitchButton from './SwitchButton';
import Button from './Button';
import BigWin from './BigWin';

import DynamicData from './DynamicData';
import TextPanel from './TextPanel';
import GameConfig from '../libraries/GameConfig';
import NetworkController from '../network/NetworkController';

import {
    GAME_DATA_KEY,
    GAME_PROPERTY_KEY,
} from '../Constants';
import TurboButton from './TurboButton';
import SpinButton from './SpinButton';

/**
 * @class MobileUI
 * @extends {UI}
 * @classdesc This class is contains all the UI components that are rendered
 * on the bottom panel in the game in mobile devices
 */
class MobileUI extends UI {
    /**
     * Creates an instance of MobileUI.
     * @param {number} [x=0]
     * @param {number} [y=0]
     * @param {*} [options={}] to set positions of spin button and panel
     * @param {MobileUI} [previouUiConfig=null] if any previous ui config found to set button states
     */
    constructor(x = 0, y = 0, options = {}, previouUiConfig = null) {
        super(x, y);

        this.expandingBookPosition = {
            start: {
                x: 0,
                y: -230,
            },
            end: {
                x: -195,
                y: -15,
                scale: 0.75,
            },
        };

        if (APP.layout.orientation === 'portrait') {
            this.expandingBookPosition.end.x = 0;
            this.expandingBookPosition.end.y = 40;
        }

        this.mobileButtonState = {
            normal: 0,
            hover: 0,
            pressed: 1,
            disable: 2,
        };

        /**
         * use to set any other button which are not part of UI but behave same
         * as other UI buttons.
         */
        this.extraButtons = {};

        // use default options if options not provided
        const defaultOptions = {
            bottomPanelPosition: 500,
            spinButtonPosition: {
                x: 445,
                y: -313,
            },
            spinButtonScale: 1.4,
            spinButtonParent: this,
            spinButtonZindex: 10,
        };

        // override defaultOption with provided options
        this.options = Object.assign(defaultOptions, options);

        /**
         * @type MobileUI
         * set previouse UI configuration data if any found.
         */
        this.config = previouUiConfig;

        /**
         * @type GambleGame
         */
        this.gambleGameScreen = null;

        /**
         * if user already selected side of spin button
         * then do not display other side of button on changing orientation back to landscape
         */
        this.selectedSpinButtonSide = '';

        if (previouUiConfig && previouUiConfig.selectedSpinButtonSide) {
            this.selectedSpinButtonSide = previouUiConfig.selectedSpinButtonSide;
        }
    }

    /**
     * @override
     * @description disable all the buttons
     */
    disableButtons() {
        super.disableButtons();
        this.buttons.turboButton.enable();

        // for (const key in this.extraButtons) {
        //     this.extraButtons[key].disable();
        // }
    }

    /**
     * @override
     * @description enables those buttons that were disabled during spin action
     * by user
     */
    enableButtons() {
        for (const key in this.buttons) {
            const button = this.buttons[key];
            if (button === this.buttons.autoButton || button === this.buttons.turbo || button === this.buttons.options) { continue; }
            if (GameData.getProperty(GAME_PROPERTY_KEY.AUTOSPIN) && (button === this.buttons.info)) {
                continue;
            }
            button.enable();
        }

        if (!GameData.getProperty(GAME_PROPERTY_KEY.AUTOSPIN)) {
            for (const key in this.extraButtons) {
                this.extraButtons[key].enable();
            }
        }

        if (!(GameData.getProperty(GAME_PROPERTY_KEY.AUTOSPIN) || GameData.getProperty(GAME_PROPERTY_KEY.FREEGAME) || DynamicData.getData(GAME_DATA_KEY.SPECIALSPIN).freeSpins > 0)) {
            for (const key in this.switchers) {
                const sw = this.switchers[key];
                sw.enable();
            }
        }
    }

    /**
     * remove resources used by this class
     * @override
     */
    safeRemove() {
        APP.layout.off('fitlayout', this.resizeUI, this);
        if (this.spinBackground) {
            this.spinBackground.safeRemove();
        }
        if (this.spinBackgroundLeft) {
            this.spinBackgroundLeft.safeRemove();
        }
        for (const key in this.buttonsGamble) {
            const btn = this.buttonsGamble[key];
            btn.safeRemove();
        }
        super.safeRemove();
    }

    /**
     * resize ui background as per orientation and screen width and height
     * @override
     * @param {*} calculatedSize
     */
    resizeUI(calculatedSize = APP.layout.calculatedSize) {
        const screenWidth = calculatedSize.screenWidth || 1280;
        const screenHeight = calculatedSize.screenHeight || 1280;
        if (APP.layout.orientation === 'portrait') {
            /**
             * set position as frame height passing in options
             * add 75px extra as bottom panel background
             * because of anchor point is middle
             */
            this.position.set(0, (-80 - (screenHeight / 2) + this.options.bottomPanelPosition));
            this.spinPanelBackground.scale.set(0.65, (screenHeight - 480) / this.spinPanelBackground.asset.height);

            this.buttonContainer.position.set(
                0, (this.spinPanelBackground.scale.y * this.spinPanelBackground.asset.height) / 2,
            );

            this.bottomYellowPanel.position.set(
                0, this.spinPanelBackground.scale.y * this.spinPanelBackground.asset.height - this.panelBackground.asset.height,
            );
        } else {
            this.panelBackground.scale.set((screenWidth / this.panelBackground.asset.width) * 1.20, 2);
        }
    }

    selectRightSide() {
        // if left spin button found then remove it
        if (!this.selectedSpinButtonSide && this.spinBackgroundLeft) {
            this.spinBackgroundLeft.safeRemove();
            delete this.spinBackgroundLeft;
            delete this.spinLeftButton;
            this.selectedSpinButtonSide = 'right';// set as right spin button is selected

            this.buttonsGamble.startGambleLeft.safeRemove();
            delete this.buttonsGamble.startGambleLeft;
        }
    }

    selectLeftSide() {
        // if right spin button found then remove it
        if (!this.selectedSpinButtonSide && this.spinBackground) {
            this.spinBackground.safeRemove();
            this.spinBackground = this.spinBackgroundLeft;

            this.buttonsGamble.startGamble.safeRemove();
            this.buttonsGamble.startGamble = this.buttonsGamble.startGambleLeft;

            this.buttonsGamble.collect = this.buttonsGamble.collectLeft;

            if (this.spinLeftButton) {
                // use left spin button sprite and object as normal spin button
                this.buttons.spin = this.spinLeftButton;
            }

            this.selectedSpinButtonSide = 'left';// set as left spin button is selected

            delete this.buttonsGamble.startGambleLeft;
            delete this.buttonsGamble.collectLeft;
            delete this.spinLeftButton;
            delete this.spinBackgroundLeft;
        }
    }

    /**
     * Add buttons, background, and mange their behaviour for bottom panel as orientation found.
     * @override
     */
    _init() {
        const gambleButtonState = {
            normal: 0,
            hover: 0,
            pressed: 0,
            disable: 3,
        };
        if (APP.layout.orientation === 'portrait') {
            // set panel background
            this.spinPanelBackground = this.addChild(APP.library.getSprite('buttons/buttons/portrait-bg'));
            this.spinPanelBackground.anchor.set(0.5, 0); // as its scale from top to down

            this.buttonContainer = this.addChild(new Sprite());
            this.buttonContainer.scale.set(1.2);

            // auto spin button
            this.buttons.autoButton = this.buttonContainer.addChild(new SwitchButton('buttons/buttons/autospin', 155, -160, 1.5, null));
            this.buttons.autoButton.hitArea = new PIXI.Circle(0, 0, 46);

            // max bet button
            this.switchers.maxbet = this.buttonContainer.addChild(new Button('buttons/buttons/max-bet', -155, -160, 1.5, null, this.mobileButtonState));
            this.switchers.maxbet.hitArea = new PIXI.Circle(0, 0, 46);

            this.buttons.spin = this.buttonContainer.addChild(new SpinButton('buttons/buttons/spin', 0, -190, 1.5));
            this.buttons.spin.hitArea = new PIXI.Circle(0, 0, 63);

            this.buttonsGamble.collect = this.buttonContainer.addChild(new Button('buttons/buttons/collect', 0, -262, 1.5, () => {
                NetworkController.takeWinAmount();
                this.collectCoins();
                this.switchToRegularGame();
            }, gambleButtonState));
            this.buttonsGamble.collect.hitArea = new PIXI.RoundedRectangle(-75, -77, 150, 150, 75);
            this.buttonsGamble.collect.text = Static.writeNativeText(this.buttonsGamble.collect, I18.f('take'), 'lato-regular', 0, 30, {
                fill: 0xfcd781,
                align: ALIGN.CENTER,
                weight: 'bold',
                size: 20,
            });
            Static.setScaleToFitWidthNativeText(this.buttonsGamble.collect.text, 100, 0.5, 1);

            this.buttonsGamble.startGamble = this.buttonContainer.addChild(new Button('buttons/buttons/gamble', 0, -115, 1.5, () => {
                for (const key in this.buttonsGamble) {
                    const btn = this.buttonsGamble[key];
                    btn.visible = false;
                }
                this.startGambleGame(this.buttonContainer);
            }, gambleButtonState));
            this.buttonsGamble.startGamble.hitArea = new PIXI.RoundedRectangle(-56, -47, 110, 85, 15);
            this.buttonsGamble.startGamble.text = Static.writeNativeText(this.buttonsGamble.startGamble, I18.f('gamble'), 'lato-regular', 0, -30, {
                fill: 0xfcd781,
                align: ALIGN.CENTER,
                weight: 'bold',
                size: 20,
            });


            // panel for displaying messages
            this.informPanel = this.buttonContainer.addChild(this.addChild(APP.library.getSprite('buttons/buttons/win')));
            this.informPanel.position.set(0, 165);
            this.informPanel.scale.set(1.5);
            this.informPanel.winText = Static.writeNativeText(this.informPanel, I18.f('win'), 'lato-regular', 0, -42, {
                fill: 0x3e0700,
                size: 22,
                stroke: 0xfcd781,
                strokeThickness: 8,
                align: ALIGN.CENTER,
                weight: 'bold',
            });

            // panel for displaying currrent bet
            this.betPanel = this.buttonContainer.addChild(APP.library.getSprite('buttons/buttons/win'));
            this.betPanel.position.set(0, 15);
            this.betPanel.scale.set(1.5);
            this.betPanel.betText = Static.writeNativeText(this.betPanel, I18.f('bet'), 'lato-regular', 0, -42, {
                fill: 0x3e0700,
                size: 22,
                stroke: 0xfcd781,
                strokeThickness: 8,
                align: ALIGN.CENTER,
                weight: 'bold',
            });

            // increase bet button
            this.switchers.s1 = this.buttonContainer.addChild(new Button('buttons/buttons/plus', 270, 15, 1.5, null, this.mobileButtonState));
            this.switchers.s1.hitArea = new PIXI.RoundedRectangle(-20, -30, 40, 52, 15);

            // decrease bet button
            this.switchers.s2 = this.buttonContainer.addChild(new Button('buttons/buttons/minus', -270, 15, 1.5, null, this.mobileButtonState));
            this.switchers.s2.hitArea = new PIXI.RoundedRectangle(-20, -30, 40, 52, 15);

            this.bottomYellowPanel = this.addChild(new Sprite());
            this.bottomYellowPanel.position.set(0, 5);
            this.panelBackground = this.bottomYellowPanel.addChild(APP.library.getSprite('buttons/buttons/background'));
            this.panelBackground.scale.set(2);
            this.buttons.info = this.bottomYellowPanel.addChild(new Button('buttons/buttons/info-small', -260, 0, 1.5, () => this.openInfo()));
            this.buttons.turboButton = this.bottomYellowPanel.addChild(new TurboButton('buttons/buttons/fastplay', -320, 0, 1.5, 'isTurboChecked'));
            this.buttons.options = this.bottomYellowPanel.addChild(new Button('buttons/buttons/options', -380, 0, 1.5));

            this.balance = this.bottomYellowPanel.addChild(new Sprite());
            this.balance.position.set(250, 0);
            this.balance.balance = Static.writeNativeText(this.balance, `${I18.f('balance')} :`, 'lato-regular', 0, 0, {
                fill: 0xfcd781,
                size: 22,
                align: ALIGN.RIGHT,
                weight: 'bold',
            });
            this.balance.amount = DynamicData.writeNativeText(this.balance, GAME_DATA_KEY.COINS, 'lato-regular', 2, 0, {
                spaceDivider: true,
                fill: 0xfcd781,
                align: ALIGN.LEFT,
                weight: 'bold',
                size: 22,
            }, null, {
                width: 140,
                minScale: 0.5,
                maxScale: 1,
            });
        } else {
            // set panel background
            this.panelBackground = this.addChild(APP.library.getSprite('buttons/buttons/background'));
            this.panelBackground.position.set(0, 0);

            // spin button and its background
            this.spinBackground = this.options.spinButtonParent.addChild(new Sprite());
            this.spinBackground.zIndex = this.options.spinButtonZindex;

            this.buttons.spin = this.spinBackground.addChild(new SpinButton('buttons/buttons/spin',
                this.options.spinButton.x, this.options.spinButton.y, this.options.spinButton.scale));
            this.buttons.spin.hitArea = new PIXI.Circle(0, 0, 63);

            // set auto spin button
            this.buttons.autoButton = this.spinBackground.addChild(new SwitchButton('buttons/buttons/autospin',
                this.options.autoButton.x, this.options.autoButton.y, this.options.autoButton.scale, null, this.mobileButtonState));
            this.buttons.autoButton.hitArea = new PIXI.Circle(0, 0, 46);

            // set max bet button
            this.switchers.maxbet = this.spinBackground.addChild(new Button('buttons/buttons/max-bet',
                this.options.maxbet.x, this.options.maxbet.y, this.options.maxbet.scale, null, this.mobileButtonState));
            this.switchers.maxbet.hitArea = new PIXI.Circle(0, 0, 46);

            this.buttons.info = this.spinBackground.addChild(new Button('buttons/buttons/info',
                this.options.info.x, this.options.info.y, this.options.info.scale, () => this.openInfo(), this.mobileButtonState));
            this.buttons.info.hitArea = new PIXI.Circle(0, 0, 27);

            this.buttonsGamble.collect = this.spinBackground.addChild(new Button('buttons/buttons/collect',
                this.options.spinButton.x, this.options.spinButton.y - 34, this.options.spinButton.scale, () => {
                    this.selectRightSide();
                    NetworkController.takeWinAmount();
                    this.collectCoins();
                    this.switchToRegularGame();
                }, gambleButtonState));
            this.buttonsGamble.collect.hitArea = new PIXI.RoundedRectangle(-63, -15, 126, 63, 32);
            this.buttonsGamble.collect.text = Static.writeNativeText(this.buttonsGamble.collect, I18.f('take'), 'lato-regular', 0, 30, {
                fill: 0xfcd781,
                align: ALIGN.CENTER,
                weight: 'bold',
                size: 20,
            });
            Static.setScaleToFitWidthNativeText(this.buttonsGamble.collect.text, 100, 0.5, 1);

            this.buttonsGamble.startGamble = this.spinBackground.addChild(new Button('buttons/buttons/gamble',
                this.options.spinButton.x, this.options.spinButton.y + 34, this.options.spinButton.scale, () => {
                    this.selectRightSide();
                    for (const key in this.buttonsGamble) {
                        const btn = this.buttonsGamble[key];
                        btn.visible = false;
                    }
                    this.startGambleGame();
                }, gambleButtonState));
            this.buttonsGamble.startGamble.hitArea = new PIXI.RoundedRectangle(-63, -47, 126, 63, 32);
            this.buttonsGamble.startGamble.text = Static.writeNativeText(this.buttonsGamble.startGamble, I18.f('gamble'), 'lato-regular', 0, -30, {
                fill: 0xfcd781,
                align: ALIGN.CENTER,
                weight: 'bold',
                size: 20,
            });

            // if left side button selected then change right side spin button position to left
            if (this.selectedSpinButtonSide === 'left') {
                this.spinBackground.position.set(-this.options.spinButtonPosition.x, this.options.spinButtonPosition.y);
                this.buttonsGamble.startGamble.position.set(-this.buttonsGamble.startGamble.x, this.buttonsGamble.startGamble.y);
            }

            // only add left side button if user not select any of the side
            if (!this.selectedSpinButtonSide) {
                this.spinBackgroundLeft = this.options.spinButtonParent.addChild(new Sprite());
                this.spinBackgroundLeft.zIndex = this.options.spinButtonZindex;
                this.spinBackgroundLeft.scale.set(this.options.spinButtonScale);
                this.spinBackgroundLeft.position.set(-this.options.spinButtonPosition.x, this.options.spinButtonPosition.y);
                this.spinLeftButton = this.spinBackgroundLeft.addChild(new MobileSpinButton('buttons/buttons/spin', 0, -7, 0.55));
                this.spinLeftButton.hitArea = new PIXI.RoundedRectangle(-75, -77, 150, 150, 75);

                this.buttonsGamble.collectLeft = this.spinBackgroundLeft.addChild(new Button('buttons/buttons/collect', 0, -7, 0.55, () => {
                    this.selectLeftSide();
                    NetworkController.takeWinAmount();
                    this.collectCoins();
                    this.switchToRegularGame();
                }, gambleButtonState));
                this.buttonsGamble.collectLeft.hitArea = new PIXI.RoundedRectangle(-75, -77, 150, 150, 75);

                this.buttonsGamble.startGambleLeft = this.options.spinButtonParent.addChild(new Button('buttons/buttons/gamble', -385, -115, 0.75, () => {
                    this.selectLeftSide();
                    for (const key in this.buttonsGamble) {
                        const btn = this.buttonsGamble[key];
                        btn.visible = false;
                    }
                    this.startGambleGame();
                }, gambleButtonState));
                this.buttonsGamble.startGambleLeft.hitArea = new PIXI.RoundedRectangle(-56, -47, 110, 85, 15);
            }


            this.buttons.turboButton = this.addChild(new TurboButton('buttons/buttons/fastplay', -385, 0, 1, GAME_PROPERTY_KEY.TURBOCHECKED));
            this.buttons.options = this.addChild(new Button('buttons/buttons/options', -425, 0, 1));

            // panel for displaying messages
            this.informPanel = this.addChild(this.addChild(APP.library.getSprite('buttons/buttons/win')));
            this.informPanel.position.set(110, 0);
            this.informPanel.scale.set(0.75);
            this.informPanel.interactiveChildren = false;
            this.informPanel.winText = Static.writeNativeText(this.informPanel, I18.f('win'), 'lato-regular', 0, -42, {
                fill: 0x3e0700,
                size: 22,
                stroke: 0xfcd781,
                strokeThickness: 8,
                align: ALIGN.CENTER,
                weight: 'bold',
            });

            // panel for displaying currrent bet
            this.betPanel = this.addChild(APP.library.getSprite('buttons/buttons/win'));
            this.betPanel.position.set(-190, 0);
            this.betPanel.scale.set(0.75);
            this.betPanel.betText = Static.writeNativeText(this.betPanel, I18.f('bet'), 'lato-regular', 0, -42, {
                fill: 0x3e0700,
                size: 22,
                stroke: 0xfcd781,
                strokeThickness: 8,
                align: ALIGN.CENTER,
                weight: 'bold',
            });

            // set increase bet button
            this.switchers.s1 = this.addChild(new Button('buttons/buttons/plus', -45, 0, 0.75, null, this.mobileButtonState));
            this.switchers.s1.hitArea = new PIXI.Circle(0, 0, 27);

            // set decrease bet button
            this.switchers.s2 = this.addChild(new Button('buttons/buttons/minus', -335, 0, 0.75, null, this.mobileButtonState));
            this.switchers.s2.hitArea = new PIXI.Circle(0, 0, 27);

            this.balance = this.addChild(new Sprite());
            this.balance.position.set(325, 0);
            this.balance.balance = Static.writeNativeText(this.balance, `${I18.f('balance')} :`, 'lato-regular', 0, 0, {
                fill: 0xfcd781,
                size: 16,
                align: ALIGN.RIGHT,
                weight: 'bold',
            });
            this.balance.amount = DynamicData.writeNativeText(this.balance, GAME_DATA_KEY.COINS, 'lato-regular', 2, 0, {
                spaceDivider: true,
                fill: 0xfcd781,
                align: ALIGN.LEFT,
                weight: 'bold',
                size: 16,
            }, null, {
                width: 120,
                minScale: 0.5,
                maxScale: 1,
            });

            // to change spinLeftButton on same state as normal spin button
            this.buttons.spin.on('statechange', () => {
                if (this.spinLeftButton) {
                    this.spinLeftButton.isDisabled = this.buttons.spin.isDisabled;
                    this.spinLeftButton.switchState(this.buttons.spin.state);
                }
            });
        }

        this.switchToRegularGame();
        // to set preconfigured states of buttons if any previous UI is found.
        this._setConfig();
        this._setClickAction();

        this.resizeUI(APP.layout.calculatedSize);
        APP.layout.on('fitlayout', this.resizeUI, this);
    }

    _setClickAction() {
        // display message of text panel on this panel
        this.informPanel.informString = TextPanel.writeText(this.informPanel, 'lato-regular', 0, 0);

        // display current bet amount on this panel
        this.betPanel.bet = DynamicData.writeNativeText(this.betPanel,
            GAME_DATA_KEY.TOTALBET,
            'lato-regular',
            0, 0, {
            spaceDivider: true,
            fill: 0xfcd781,
            align: ALIGN.CENTER,
            weight: 'bold',
            size: 22,
        }, null, {
            width: 175,
            minScale: 0.65,
            maxScale: 1,
        });

        this.switchers.maxbet.clickAction = () => {
            NetworkController.maxBet((response) => {
                DynamicData.setValue(GAME_DATA_KEY.TOTALBET, response.totalBet);
                DynamicData.setValue(GAME_DATA_KEY.BET, response.currentBet);
            });
        };

        this.switchers.s1.clickAction = () => {
            super.setNextBet(1);
        };

        this.switchers.s2.clickAction = () => {
            super.setNextBet(-1);
        };

        this.buttons.turboButton.clickAction = () => {
            const isTurboChecked = GameData.getProperty(GAME_PROPERTY_KEY.TURBOCHECKED);
            NetworkController.updateTuroEvent(isTurboChecked, false);
        };
    }

    /**
     * @name _betChange
     * @param  {Number} currentBet amount to place as current bet
     * @description change current total bet
     */
    _betChange(currentBet) {
        this.isClicked = false;
        this.buttons.spin.switchState(0);
        DynamicData.setValue(GAME_DATA_KEY.BET, currentBet);
    }


    // set configuration of buttons and switchers as previouse UI config if found.
    _setConfig() {
        if (this.config) {
            this.buttons.autoButton.switchState(this.config.buttons.autoButton.state);

            this.buttons.spin.isDisabled = this.config.buttons.spin.isDisabled;
            this.buttons.spin.switchState(this.config.buttons.spin.state);

            for (const key in this.switchers) {
                if (this.switchers.hasOwnProperty(key)) {
                    this.switchers[key].gotoAndStop(this.config.switchers[key].currentFrame);
                    this.switchers[key].isDisabled = this.config.switchers[key].isDisabled;
                }
            }

            if (this.config.informPanelFG.visible && this.config.expandingBook) {
                this.informPanelFG = this.config.informPanelFG;
                this.expandingBook = this.addChild(this.config.expandingBook.clone());

                MobileUI.showFreeWinText(this.expandingBook);

                this._setBookToFinalPosition();

                if (this.config.expandingCallback) {
                    this.config.expandingCallback();
                }
                if (this.config.popupCallback && !this.config.expandingCallback) {
                    this.config.popupCallback();
                }
            } else if (this.config.informPanelFG.visible) {
                this.informPanelFG = this.config.informPanelFG;
                this.openExpandingWindow(this.config.popupCallback);
            }
            GameData.setProperty('isPopupDialogueOpen', false);

            if (GameData.getProperty('isGambleGame')) {
                this.gambleGameScreen = this.config.gambleGameScreen;
                this.gambleGameScreen.side = this.config.selectedSpinButtonSide;
                this.gambleGameScreen.collectButtonParent = this.spinBackground;
                this.gambleGameScreen.exitHandler = (selectedSide) => {
                    if (selectedSide === 'right') {
                        this.selectRightSide();
                    } else if (selectedSide === 'left') {
                        this.selectLeftSide();
                    }
                    this.endGambleGame();
                };
                this.gambleGameScreen.changeOrientation();
                this.startGambleGame(null, true);
            }

            if ((this.config.buttonsGamble.startGamble
                && this.config.buttonsGamble.startGamble.visible)
                || (this.config.buttonsGamble.startGambleLeft
                    && this.config.buttonsGamble.startGambleLeft.visible)
            ) {
                this.switchToGambleGame();
            }
        }

        // set to undefined, it is not required
        this.config = undefined;
    }


    /**
     * @name switchToGambleGame
     * @description swith to regular gamble view zone
     */
    switchToGambleGame() {
        this.buttons.spin.visible = false;
        if (this.spinLeftButton) {
            this.spinLeftButton.visible = false;
        }

        for (const key in this.buttonsGamble) {
            const btn = this.buttonsGamble[key];
            btn.visible = true;
        }
    }

    /**
     * @name startGambleGame
     * @description start gamble game and gamble view zone
     */
    startGambleGame(spinButtonParent, isGambleActive = false) {
        if (APP.layout.orientation === 'landscape') {
            if (this.spinBackground) {
                this.spinBackground.visible = false;
            }
            if (this.spinBackgroundLeft) {
                this.spinBackgroundLeft.visible = false;
            }
        } else {
            this.buttons.spin.visible = false;
        }
        this.disableButtons();

        if (!isGambleActive) {
            BigWin.stop(() => {
                this.gambleGameScreen = this.options.gambleParent.addChild(new GambleGame(this.selectedSpinButtonSide, spinButtonParent));
                this.gambleGameScreen.exitHandler = (selectedSide) => {
                    if (selectedSide === 'right') {
                        this.selectRightSide();
                    } else if (selectedSide === 'left') {
                        this.selectLeftSide();
                    }
                    this.endGambleGame();
                };
            });
        }
    }

    /**
     * @name endGambleGame
     * @description end gamble game and close gamble view zone
     */
    endGambleGame() {
        if (!this.gambleGameScreen) { return; }

        if (APP.layout.orientation === 'landscape') {
            if (this.spinBackground) {
                this.spinBackground.visible = true;
            }
            if (this.spinBackgroundLeft) {
                this.spinBackgroundLeft.visible = true;
            }
        } else {
            this.buttons.spin.visible = true;
        }

        this.gambleGameScreen.finish();
        this.collectCoins();
        this.enableButtons();
        this.switchToRegularGame();
        this.gambleGameScreen = null;
    }

    _setBookToFinalPosition() {
        this.expandingBook.position.set(this.expandingBookPosition.end.x,
            this.expandingBookPosition.end.y);

        this.expandingBook.scale.set(this.expandingBookPosition.end.scale * 0.8);

        const symbolNumber = GameData.getProperty('expandingSymbol');

        const rightPage = this.expandingBook.addChild(new Sprite());

        const symbolAssets = [
            'expanding/Low_10_symbol',
            'expanding/Low_J_symbol',
            'expanding/Low_Q_symbol',
            'expanding/Low_K_symbol',
            'expanding/Low_A_symbol',
            'expanding/Hi_05',
            'expanding/Hi_04',
            'expanding/Hi_03',
            'expanding/Hi_02',
        ];

        let src = symbolAssets[symbolNumber];
        let asset;
        if (Array.isArray(src)) {
            src = src[0];
            asset = src.map(e => APP.library.getAsset(e));
        } else {
            asset = APP.library.getAsset(src);
        }
        const symbol = rightPage.addChild(new Sprite(asset));

        symbol.gotoAndStop(0);
        symbol.zIndex = 50;
        symbol.alpha = 1;
        symbol.scale.set(0.35);
        symbol.position.set(55, -35);
        symbol.play();

        const randomPaytable = rightPage.addChild(new Sprite());
        const income = GameConfig.symbolIncome[symbolNumber].slice(0).map(e => e * DynamicData.getValue('bet'));
        const yPositions = income[0] === 0 ? [0, 55, 40, 25] : [65, 50, 35, 20];

        const maxWidth = 0;
        for (let i = income.length - 1; i >= 0; i--) {
            const val = income[i];
            if (val) {
                Static.writeText(randomPaytable, i + 2, 'font_pay_table_num_07', 28, yPositions[i], {
                    scale: 0.9,
                });
                Static.writeNativeText(randomPaytable, '-', 'myriad', 35, yPositions[i] - 6, {
                    fill: '#370202',
                    weight: 'bold',
                    align: ALIGN.LEFT,
                    valign: VALIGN.BOTTOM,
                    size: 10,
                });
                Static.writeNativeText(randomPaytable, val, 'myriad', 41, yPositions[i] - 5, {
                    fill: '#370202',
                    weight: 'bold',
                    size: 10,
                    align: ALIGN.LEFT,
                    valign: VALIGN.BOTTOM,
                    spaceDivider: true,
                });
            }
        }
        randomPaytable.scale.set(1.1);
        randomPaytable.position.set(0, -23);
    }
}

export default MobileUI;
